import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Marketplace, MarketplaceSource, Plugin } from "@/types";

function useClient() {
  const { session, logout } = useAuth();
  return () => createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}

// #232 (UC-4): Marketplace-Eingaben. Keine Secrets — Plugin-Quellen sind öffentlich.
export interface AddMarketplaceInput {
  name: string;
  source: MarketplaceSource;
  scope?: string; // default platform (Backend)
  autoUpdate?: boolean;
}

function useInvalidateCatalog() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: queryKeys.list("marketplaces") });
    // #290 (Review-Fund, HIGH): der ganze plugins-Baum, nicht nur "list" — sonst
    // aktualisiert das Plugin-DETAIL (["resources","plugins","detail",…]) die eigenen
    // Freigabe-Schalter nicht: Toast sagt „freigegeben", die Checkbox bleibt leer und
    // der Owner-Schalter disabled, bis staleTime abläuft.
    qc.invalidateQueries({ queryKey: queryKeys.resource("plugins") });
    // #288: Eine Freigabe-Entscheidung ändert auch, was pro Agent aktivierbar ist — die
    // Per-Agent-Listen hängen als Sub-Resource unter "agents" und würden sonst veraltet
    // weiterzeigen, dass man ein soeben gesperrtes Plugin noch einschalten kann.
    qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
  };
}

// #292 (UC-4): Meta-Änderung — läuft serverseitig IMMER über einen Entwurf. Die Antwort
// sagt, was passiert ist (mode/submitted) — der Erfolgstext muss das ehrlich wiedergeben.
export interface UpdatePluginMetaResult {
  mode: "draft-created" | "draft-updated";
  draftBranch: string;
  submitted: boolean;
}

export function useUpdatePluginMeta(): UseMutationResult<
  UpdatePluginMetaResult,
  Error,
  // #294 (UC-0): targetDraftBranch patcht GENAU diesen offenen Entwurf (Freigeben-Dialog).
  { marketplaceId: string; pluginName: string; description?: string; version?: string; targetDraftBranch?: string }
> {
  const getClient = useClient();
  const qc = useQueryClient();
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: ({ marketplaceId, pluginName, ...patch }) =>
      getClient().patch<UpdatePluginMetaResult>(
        `/plugins/${encodeURIComponent(marketplaceId)}/${encodeURIComponent(pluginName)}/meta`,
        patch,
      ),
    onSuccess: () => {
      invalidate();
      // die Änderung lebt in einem ENTWURF → Entwurfs-Listen + pending-Sichten auffrischen
      qc.invalidateQueries({ queryKey: ["resources", "git-content"] });
      qc.invalidateQueries({ queryKey: queryKeys.resource("marketplaces") });
    },
  });
}

export function useAddMarketplace(): UseMutationResult<Marketplace, Error, AddMarketplaceInput> {
  const getClient = useClient();
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: (input) => getClient().post<Marketplace>("/marketplaces", input),
    onSuccess: invalidate,
  });
}

// #294 (UC-1): der eine nachträglich änderbare Auslieferungs-Schalter der Quelle.
export function useUpdateMarketplace(): UseMutationResult<Marketplace, Error, { id: string; autoUpdate: boolean }> {
  const getClient = useClient();
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: ({ id, autoUpdate }) => getClient().patch<Marketplace>(`/marketplaces/${encodeURIComponent(id)}`, { autoUpdate }),
    onSuccess: invalidate,
  });
}

// #294 (UC-3): gezieltes in-place-Update EINES Plugins im laufenden Brain (idempotent —
// CC skippt Gleichstand). Antwort trägt die danach installierte Version.
export function useUpdatePluginInBrain(): UseMutationResult<
  { installedVersion: string | null },
  Error,
  { agentId: string; marketplaceId: string; pluginName: string }
> {
  const getClient = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, marketplaceId, pluginName }) =>
      getClient().post<{ installedVersion: string | null }>(
        `/agents/${encodeURIComponent(agentId)}/plugins/${encodeURIComponent(marketplaceId)}/${encodeURIComponent(pluginName)}/update`,
      ),
    onSuccess: (_res, { agentId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.subResource("agents", agentId, "plugins") });
    },
  });
}

// #287: `useSyncMarketplace` ist ersatzlos entfallen — der Katalog wird live gelesen, es gibt
// nichts nachzuziehen. Ein Refetch der Liste zeigt sofort den aktuellen Manifest-Stand.

// #232 (UC-4): Marketplace entfernen. Cascade: unsere Entscheidungen + alle Zuweisungen +
// Config-Republish bei jedem betroffenen Agent (Backend).
// #290 (UC-5): `deleteRepo` nimmt bei EIGENEN Katalogen das Gitea-Repo mit (Archiv-
// Rotation serverseitig) — sonst bleibt nach dem Remove eine unsichtbare Repo-Leiche.
// String-Eingabe bleibt gültig (Bestands-Aufrufer ohne Checkbox).
export function useRemoveMarketplace(): UseMutationResult<void, Error, string | { id: string; deleteRepo?: boolean }> {
  const getClient = useClient();
  const invalidate = useInvalidateCatalog();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => {
      const { id, deleteRepo } = typeof input === "string" ? { id: input, deleteRepo: false } : input;
      return getClient().delete<void>(`/marketplaces/${id}${deleteRepo ? "?deleteRepo=true" : ""}`);
    },
    onSuccess: () => {
      invalidate();
      // #290 (Review-Fund): das Remove ändert IMMER die Repo-Whitelist (mp-/plg- fallen
      // heraus, mit deleteRepo zusätzlich das Inventar) — sonst bleibt das weggerollte
      // Repo in der /repositories-Sidebar anklickbar.
      void qc.invalidateQueries({ queryKey: ["resources", "git-content"] });
    },
  });
}

// #232 (UC-5) / #287: Admin-Freigabe eines Plugins für den Owner-Self-Service. Anker im Pfad
// ist (marketplaceId, pluginName) — es gibt keine Plugin-Id mehr.
export function useSetPluginOwnerAvailable(): UseMutationResult<
  Plugin,
  Error,
  { marketplaceId: string; pluginName: string; ownerAvailable: boolean }
> {
  const getClient = useClient();
  const invalidate = useInvalidateCatalog();
  const patchCaches = usePatchPluginCaches();
  return useMutation({
    mutationFn: ({ marketplaceId, pluginName, ownerAvailable }) =>
      getClient().patch<Plugin>(`/plugins/${marketplaceId}/${encodeURIComponent(pluginName)}`, { ownerAvailable }),
    onSuccess: (item) => {
      patchCaches(item);
      invalidate();
    },
  });
}

// #288 (UC-1): Plattform-Freigabe — „diesem Plugin vertrauen wir". Ohne sie ist das Plugin
// weder zuweisbar noch auslieferbar; das Zurücknehmen ist der Not-Aus (wirkt beim nächsten
// Push, ohne Git-Änderung und ohne Freigabe-Runde).
// #291 (Arbens Live-Fund): die aggregierte 287er-Liste refetcht nach einer Entscheidung
// ALLE Quellen (inkl. gestörter mit Netzwerk-Timeout) — bis dahin zeigte die Checkbox den
// ALTEN Zustand. Deshalb wird die Server-Antwort (das entschiedene Item) SOFORT punktuell
// in alle gecachten Katalog-Sichten geschrieben; der Refetch läuft danach im Hintergrund.
function usePatchPluginCaches() {
  const qc = useQueryClient();
  return (item: Plugin) => {
    const ersetzen = (data: unknown): unknown => {
      if (!data || typeof data !== "object") return data;
      const d = data as { plugins?: Plugin[]; plugin?: Plugin };
      if (Array.isArray(d.plugins)) {
        return {
          ...d,
          plugins: d.plugins.map((p) =>
            p.marketplaceId === item.marketplaceId && p.name === item.name ? { ...p, ...item } : p,
          ),
        };
      }
      if (d.plugin && d.plugin.marketplaceId === item.marketplaceId && d.plugin.name === item.name) {
        return { ...d, plugin: { ...d.plugin, ...item } };
      }
      return data;
    };
    qc.setQueriesData({ queryKey: ["resources", "plugins"] }, ersetzen);
    qc.setQueriesData({ queryKey: ["resources", "marketplaces"] }, ersetzen);
  };
}

export function useSetPluginApproved(): UseMutationResult<
  Plugin,
  Error,
  { marketplaceId: string; pluginName: string; approved: boolean }
> {
  const getClient = useClient();
  const invalidate = useInvalidateCatalog();
  const patchCaches = usePatchPluginCaches();
  return useMutation({
    mutationFn: ({ marketplaceId, pluginName, approved }) =>
      getClient().patch<Plugin>(`/plugins/${marketplaceId}/${encodeURIComponent(pluginName)}`, { approved }),
    // Auch die Per-Agent-Listen: eine Sperre ändert dort sofort, was aktivierbar ist.
    onSuccess: (item) => {
      patchCaches(item);
      invalidate();
    },
  });
}

// #288 (UC-6): Massen-Freigabe — der Regelfall beim Befüllen eines Katalogs. Serverseitig
// bekommt trotzdem jeder Name seine eigene Entscheidung (und seinen eigenen Audit-Eintrag).
export interface BulkApproveResult {
  approved: boolean;
  done: string[];
  failed: Array<{ name: string; code: string }>;
}

export function useBulkApprovePlugins(): UseMutationResult<
  BulkApproveResult,
  Error,
  { marketplaceId: string; pluginNames: string[]; approved: boolean }
> {
  const getClient = useClient();
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: (input) => getClient().post<BulkApproveResult>("/plugins/bulk-approve", input),
    onSuccess: invalidate,
  });
}

// #232 (UC-2/4): Plugin für einen Agent enablen/disablen (scope managed|owner). Invalidiert die
// Per-Agent-Liste UND den Katalog (Verfügbarkeits-Sicht).
export function useSetAgentPlugin(
  agentId: string,
): UseMutationResult<
  void,
  Error,
  { marketplaceId: string; pluginName: string; enabled: boolean; scope?: "managed" | "owner" }
> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ marketplaceId, pluginName, enabled, scope }) =>
      getClient().put<void>(`/agents/${agentId}/plugins/${marketplaceId}/${encodeURIComponent(pluginName)}`, {
        enabled,
        scope,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.subResource("agents", agentId, "plugins") });
      qc.invalidateQueries({ queryKey: queryKeys.list("plugins") });
    },
  });
}
